import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  LISTING_MEDIA_MAX_BYTES,
  LISTING_MEDIA_MIME_ALLOWLIST,
  listingChangeSchema,
  type ListingChangePayload,
  type ListingField,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUploadListingMedia } from "./use-upload-listing-media";

// The seven-field listing edit form. Whole-form pessimistic submit
// (deal-new-page pattern): local string FormState, nothing persists until
// the partner submits the complete payload — the parent owns the mutation.
//
// URL fields are held as "" while empty and mapped to null on submit, per
// the no-transform contract in @colab/shared/schemas/marketplace.ts.
// Client-side validation = listingChangeSchema.safeParse on the trimmed
// candidate; the first issue per field renders inline. Image uploads go
// through useUploadListingMedia immediately (hidden input + button, the
// tier-column-header pattern) and only place a URL into form state.
//
// `onChange` streams the current values up so the page can drive the live
// marketplace preview card without owning the field state.

type FormState = {
  name: string;
  short_description: string;
  full_description: string;
  logo_url: string;
  hero_image_url: string;
  website_url: string;
  documentation_url: string;
};

type FieldErrors = Partial<Record<ListingField, string>>;

const ACCEPT_ATTR = LISTING_MEDIA_MIME_ALLOWLIST.join(",");

function fromPayload(p: ListingChangePayload): FormState {
  return {
    name: p.name,
    short_description: p.short_description,
    full_description: p.full_description,
    logo_url: p.logo_url ?? "",
    hero_image_url: p.hero_image_url ?? "",
    website_url: p.website_url ?? "",
    documentation_url: p.documentation_url ?? "",
  };
}

// Untrimmed live mapping for the preview; trimming happens once, on submit.
function toPayload(s: FormState): ListingChangePayload {
  return {
    name: s.name,
    short_description: s.short_description,
    full_description: s.full_description,
    logo_url: s.logo_url || null,
    hero_image_url: s.hero_image_url || null,
    website_url: s.website_url || null,
    documentation_url: s.documentation_url || null,
  };
}

export function ListingForm({
  initial,
  disabled,
  submitting,
  onSubmit,
  onChange,
}: {
  initial: ListingChangePayload;
  disabled?: boolean;
  submitting: boolean;
  onSubmit: (payload: ListingChangePayload) => void;
  onChange?: (payload: ListingChangePayload) => void;
}) {
  const [form, setForm] = useState<FormState>(() => fromPayload(initial));
  const [errors, setErrors] = useState<FieldErrors>({});
  const logoInputRef = useRef<HTMLInputElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadListingMedia();

  const isDisabled = Boolean(disabled) || submitting;
  const uploadingLogo = upload.isPending && upload.variables?.kind === "logo";
  const uploadingHero = upload.isPending && upload.variables?.kind === "hero";

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    const next = { ...form, [key]: value };
    setForm(next);
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    onChange?.(toPayload(next));
  }

  function onPickFile(kind: "logo" | "hero", e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!(LISTING_MEDIA_MIME_ALLOWLIST as readonly string[]).includes(file.type)) {
      toast.error("Image must be a PNG, JPG, WebP, or SVG");
      return;
    }
    if (file.size > LISTING_MEDIA_MAX_BYTES) {
      toast.error("Image must be 2 MB or smaller");
      return;
    }
    upload.mutate(
      { kind, file },
      {
        onSuccess: ({ url }) => {
          patch(kind === "logo" ? "logo_url" : "hero_image_url", url);
        },
      },
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;
    const candidate: ListingChangePayload = {
      name: form.name.trim(),
      short_description: form.short_description.trim(),
      full_description: form.full_description.trim(),
      logo_url: form.logo_url.trim() || null,
      hero_image_url: form.hero_image_url.trim() || null,
      website_url: form.website_url.trim() || null,
      documentation_url: form.documentation_url.trim() || null,
    };
    const parsed = listingChangeSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !next[key as ListingField]) {
          next[key as ListingField] = issue.message;
        }
      }
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit(parsed.data);
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border bg-card p-5">
      <div className="grid gap-4">
        <Field label="Name" required error={errors.name}>
          <Input
            value={form.name}
            onChange={(e) => patch("name", e.target.value)}
            placeholder="Your integration's name on the marketplace"
            maxLength={80}
            disabled={isDisabled}
          />
        </Field>

        <Field
          label="Short description"
          required
          error={errors.short_description}
          counter={`${form.short_description.length}/200`}
        >
          <Textarea
            value={form.short_description}
            onChange={(e) => patch("short_description", e.target.value)}
            rows={2}
            maxLength={200}
            placeholder="One or two sentences shown on the marketplace tile."
            disabled={isDisabled}
          />
        </Field>

        <Field
          label="Full description"
          required
          error={errors.full_description}
          counter={`${form.full_description.length}/5000`}
        >
          <Textarea
            value={form.full_description}
            onChange={(e) => patch("full_description", e.target.value)}
            rows={8}
            maxLength={5000}
            placeholder="The full story on your listing's detail page."
            disabled={isDisabled}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Website URL" error={errors.website_url}>
            <Input
              type="url"
              value={form.website_url}
              onChange={(e) => patch("website_url", e.target.value)}
              placeholder="https://"
              disabled={isDisabled}
            />
          </Field>
          <Field label="Documentation URL" error={errors.documentation_url}>
            <Input
              type="url"
              value={form.documentation_url}
              onChange={(e) => patch("documentation_url", e.target.value)}
              placeholder="https://"
              disabled={isDisabled}
            />
          </Field>
        </div>

        <Field label="Logo" error={errors.logo_url}>
          <div className="flex flex-wrap items-center gap-3">
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt="Logo preview"
                className="h-10 w-10 rounded-md border bg-muted/30 object-contain p-1"
              />
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => logoInputRef.current?.click()}
              disabled={isDisabled || uploadingLogo}
            >
              {uploadingLogo ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-2 h-3.5 w-3.5" />
              )}
              {form.logo_url ? "Replace logo" : "Upload logo"}
            </Button>
            {form.logo_url ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => patch("logo_url", "")}
                disabled={isDisabled}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : null}
            <input
              ref={logoInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => onPickFile("logo", e)}
            />
          </div>
        </Field>

        <Field label="Hero image" error={errors.hero_image_url}>
          <div className="flex flex-wrap items-center gap-3">
            {form.hero_image_url ? (
              <img
                src={form.hero_image_url}
                alt="Hero preview"
                className="h-14 w-24 rounded-md border bg-muted/30 object-cover"
              />
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => heroInputRef.current?.click()}
              disabled={isDisabled || uploadingHero}
            >
              {uploadingHero ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-2 h-3.5 w-3.5" />
              )}
              {form.hero_image_url ? "Replace image" : "Upload image"}
            </Button>
            {form.hero_image_url ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => patch("hero_image_url", "")}
                disabled={isDisabled}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : null}
            <input
              ref={heroInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => onPickFile("hero", e)}
            />
          </div>
        </Field>
      </div>

      <div className="mt-6 flex items-center justify-end border-t pt-4">
        <Button type="submit" disabled={isDisabled}>
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Submit changes for review
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  error,
  counter,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  counter?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
          {required ? <span className="ml-1 text-destructive">*</span> : null}
        </Label>
        {counter ? (
          <span className="text-[10px] tabular-nums text-muted-foreground/70">
            {counter}
          </span>
        ) : null}
      </div>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
